import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManText } from '@craft-design-system/core-components-react';
import { Card } from 'react-bootstrap';
import PopoverComponent from '../popover';
import SegmentedControl_totalMileage from '../ride_analysis_kpi/SegmentedControl_totalMileage';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Shimmer_graph from '../shimmer_graph';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';

const All_time_energythroughput = ({ value }) => {
  const { vin_short, All_time_energy_throughput } = value;


  const [chartType, setChartType] = useState(
    Array.isArray(vin_short) && vin_short.length === 1 ? 'Bar' : 'histogram'
  );
  console.log('vin_short',All_time_energy_throughput);

  const [energyThroughputChart, setEnergyThroughputChart] = useState(
    All_time_energy_throughput?.[0] ? JSON.parse(All_time_energy_throughput[0]) : { data: [], layout: {} }
  );
  const [downloadData, setDownloadData] = useState(
    All_time_energy_throughput?.[1] ? JSON.parse(All_time_energy_throughput[1]) : []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const { fetchSegmentedChart } = useSegmentedChartQuery();

  // Fetch when chartType or VIN changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: 'Energy Throughput All Time',
        });
        console.log('fetched data',data);

        // Handle both possible data structures
        const chartData = data['graph_data'] ? data['graph_data'][0] : data[0];
        const downloadData = data['graph_data'] ? data['graph_data'][1] : data[1];
        console.log(chartData, downloadData);
        
        setEnergyThroughputChart(JSON.parse(chartData));   // chart
        setDownloadData(JSON.parse(downloadData || '[]'));            // download
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setEnergyThroughputChart({ data: [], layout: {} });
        setDownloadData([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType]);

  return (
    <Card className="card_battery">
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            All time energy throughput
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row-reverse'
        }}>
          <PopoverComponent
            id="median_consumption"
            content="<b>Total Energy Throughput:</b> The total energy throughput (all time) is visualized.<br/><br/><b>Telematic Raw Data:</b><br/>• TBM3: payloaddecoded_batteryoutputenergy_value<br/>• CM4: payload_electricmessagesystem_batteryoutputenergy (BBP), payload_electricmessagesystem_batoutenergycbp (CBP)<br/><br/><b>Time Series Data:</b><br/>For single vehicles the course of total energy throughput since start of data acquisition is displayed.<br/><br/><b>Histogram:</b><br/>Each bar represents the number of vehicles that fall within a certain range (bin) of <b>total energy throughput values</b>. The latest total energy throughput value for the selected vehicle(s) is visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of total energy throughput values</b> for the selected vehicle(s). If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their latest values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box."
          />
          <Zoom
            chartData={energyThroughputChart?.data}
            chartLayout={energyThroughputChart?.layout}
            chartTitle="All time energy throughput"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`energy_throughput_${chartType.toLowerCase()}.xlsx`}
          />
        </div>
      </div>

      {/* Segmented control */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SegmentedControl_totalMileage
          selected={chartType}
          onChange={setChartType}
          vinShort={Array.isArray(vin_short) ? vin_short : [vin_short]}
        />
      </div>

      {/* Chart */}
      {isLoading ? (
        <Shimmer_graph />
      ) : energyThroughputChart?.data?.length ? (
        <div style={{ width: '100%', height: '420px' }}>
          <Plot
            data={energyThroughputChart.data}
            layout={{
              ...energyThroughputChart.layout,
              margin: { t: 10, r: 10, l: 10, b: 130 },
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler
          />
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
          >
            No data available
          </ManText>
        </div>
      )}
    </Card>
  );
};

All_time_energythroughput.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.oneOfType([PropTypes.array, PropTypes.string]).isRequired,
    energy_throughput: PropTypes.array.isRequired, // [fig_json, df_json]
  }).isRequired,
};

export default All_time_energythroughput;